/**
 * Image processing utility for product/category photos.
 *
 * Handles phone/tablet camera images safely on iOS WKWebView:
 *  - EXIF orientation correction
 *  - HEIC/HEIF → JPEG conversion
 *  - Downscale before decode when createImageBitmap is available (avoids OOM)
 *  - Quality compression to a target size
 */

const DEFAULT_TARGET_SIZE_KB = 400;
const DEFAULT_MAX_DIMENSION = 1200;
const ICON_MAX_DIMENSION = 512;
const ICON_TARGET_SIZE_KB = 200;
const INITIAL_QUALITY = 0.85;
const MIN_QUALITY = 0.4;
const MAX_INPUT_BYTES = 30 * 1024 * 1024;

const TRANSPARENCY_FORMATS = ['image/png', 'image/gif', 'image/webp', 'image/avif'];

export const IMAGE_PRESETS = {
  product: { maxDimension: DEFAULT_MAX_DIMENSION, targetSizeKb: DEFAULT_TARGET_SIZE_KB },
  icon: { maxDimension: ICON_MAX_DIMENSION, targetSizeKb: ICON_TARGET_SIZE_KB },
};

function isHeicLike(file) {
  const mime = (file?.type || '').toLowerCase();
  const name = (file?.name || '').toLowerCase();
  return mime === 'image/heic'
    || mime === 'image/heif'
    || name.endsWith('.heic')
    || name.endsWith('.heif');
}

export function revokeImagePreviewUrl(url) {
  if (url && typeof url === 'string' && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

function readExifOrientation(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const view = new DataView(e.target.result);

      if (view.getUint16(0, false) !== 0xFFD8) {
        resolve(1);
        return;
      }

      let offset = 2;
      while (offset < view.byteLength) {
        if (view.getUint16(offset, false) === 0xFFE1) {
          const exifOffset = offset + 4;
          if (view.getUint32(exifOffset, false) !== 0x45786966) {
            resolve(1);
            return;
          }
          const tiffStart = exifOffset + 6;
          const littleEndian = view.getUint16(tiffStart, false) === 0x4949;
          const ifdOffset = view.getUint32(tiffStart + 4, littleEndian) + tiffStart;
          const entries = view.getUint16(ifdOffset, littleEndian);

          for (let i = 0; i < entries; i += 1) {
            const entryOffset = ifdOffset + 2 + i * 12;
            if (view.getUint16(entryOffset, littleEndian) === 0x0112) {
              resolve(view.getUint16(entryOffset + 8, littleEndian));
              return;
            }
          }
          resolve(1);
          return;
        }
        offset += 2 + view.getUint16(offset + 2, false);
      }
      resolve(1);
    };
    reader.onerror = () => resolve(1);
    reader.readAsArrayBuffer(file.slice(0, 65536));
  });
}

function applyOrientationToCanvas(canvas, ctx, width, height, orientation) {
  if (orientation > 4) {
    canvas.width = height;
    canvas.height = width;
  } else {
    canvas.width = width;
    canvas.height = height;
  }

  switch (orientation) {
    case 2: ctx.transform(-1, 0, 0, 1, width, 0); break;
    case 3: ctx.transform(-1, 0, 0, -1, width, height); break;
    case 4: ctx.transform(1, 0, 0, -1, 0, height); break;
    case 5: ctx.transform(0, 1, 1, 0, 0, 0); break;
    case 6: ctx.transform(0, 1, -1, 0, height, 0); break;
    case 7: ctx.transform(0, -1, -1, 0, height, width); break;
    case 8: ctx.transform(0, -1, 1, 0, 0, width); break;
    default: break;
  }
}

function scaleDimensions(srcW, srcH, maxDim) {
  if (srcW <= maxDim && srcH <= maxDim) {
    return { w: srcW, h: srcH };
  }
  const ratio = Math.min(maxDim / srcW, maxDim / srcH);
  return { w: Math.round(srcW * ratio), h: Math.round(srcH * ratio) };
}

async function createImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not load the selected image.'));
    };
    img.src = objectUrl;
  });
}

async function drawToCanvas(file, maxDimension, preserveTransparency) {
  const orientation = preserveTransparency ? 1 : await readExifOrientation(file);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, {
        resizeWidth: maxDimension,
        resizeHeight: maxDimension,
        resizeQuality: 'high',
      });

      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      if (preserveTransparency) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      ctx.drawImage(bitmap, 0, 0);
      if (typeof bitmap.close === 'function') {
        bitmap.close();
      }

      return canvas;
    } catch (bitmapError) {
      console.warn('[imageUtils] createImageBitmap resize failed; falling back to Image()', bitmapError);
    }
  }

  const img = await createImageFromFile(file);
  const { w, h } = scaleDimensions(img.naturalWidth, img.naturalHeight, maxDimension);

  if (preserveTransparency) {
    canvas.width = w;
    canvas.height = h;
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
  } else {
    applyOrientationToCanvas(canvas, ctx, w, h, orientation);
    ctx.drawImage(img, 0, 0, w, h);
  }

  return canvas;
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Could not compress the selected image.'));
        return;
      }
      resolve(blob);
    }, type, quality);
  });
}

/**
 * @param {File} file
 * @param {{ maxDimension?: number, targetSizeKb?: number }} options
 */
export async function processImage(file, options = {}) {
  if (!file) {
    throw new Error('No image was selected.');
  }

  if (!file.type.startsWith('image/') && !isHeicLike(file)) {
    return { file, previewUrl: URL.createObjectURL(file) };
  }

  if (file.size > MAX_INPUT_BYTES) {
    throw new Error('That photo is too large. Try again with a smaller image or lower camera resolution.');
  }

  const maxDimension = options.maxDimension || DEFAULT_MAX_DIMENSION;
  const targetSizeKb = options.targetSizeKb || DEFAULT_TARGET_SIZE_KB;
  const preserveTransparency = !isHeicLike(file) && TRANSPARENCY_FORMATS.includes(file.type);
  const outputMime = preserveTransparency ? file.type : 'image/jpeg';
  const outputExt = outputMime === 'image/png'
    ? '.png'
    : outputMime === 'image/webp'
      ? '.webp'
      : outputMime === 'image/gif'
        ? '.gif'
        : '.jpg';

  const canvas = await drawToCanvas(file, maxDimension, preserveTransparency);

  let blob;
  if (outputMime === 'image/png' || outputMime === 'image/gif') {
    blob = await canvasToBlob(canvas, outputMime, 1);
  } else {
    let quality = INITIAL_QUALITY;
    blob = await canvasToBlob(canvas, outputMime, quality);
    while (blob.size > targetSizeKb * 1024 && quality > MIN_QUALITY) {
      quality -= 0.05;
      blob = await canvasToBlob(canvas, outputMime, quality);
    }
  }

  const newName = (file.name || 'photo').replace(/\.[^.]+$/, outputExt);
  const processedFile = new File([blob], newName, {
    type: outputMime,
    lastModified: Date.now(),
  });
  const previewUrl = URL.createObjectURL(processedFile);

  console.log(
    `[imageUtils] ${file.name}: ${(file.size / 1024).toFixed(0)} KB → ${(processedFile.size / 1024).toFixed(0)} KB (${canvas.width}×${canvas.height})`
  );

  return { file: processedFile, previewUrl };
}

/**
 * Shared file-input handler for camera/library picks.
 */
export async function processImageFileInput(file, previousPreviewUrl, options = {}) {
  if (!file) {
    return { file: null, previewUrl: '', previousPreviewUrl };
  }

  revokeImagePreviewUrl(previousPreviewUrl);
  const result = await processImage(file, options);
  return { ...result, previousPreviewUrl: result.previewUrl };
}
